import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Wallet, Loader2, AlertCircle, Clock, ArrowDownToLine } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";

interface LedgerEntry {
  id: string;
  type: string;
  level: number | null;
  amountCents: number;
  description: string | null;
  availableAt: string;
  createdAt: string;
}

interface EarningsResponse {
  balance: { totalCents: number; availableCents: number; pendingCents: number };
  minPayoutCents: number;
  canWithdraw: boolean;
  entries: LedgerEntry[];
}

function money(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  return `${sign}$${(Math.abs(cents) / 100).toFixed(2)}`;
}

const ENTRY_LABELS: Record<string, string> = {
  signup_commission: "Referral commission",
  sale_commission: "Sale commission",
  payout: "Withdrawal",
  clawback: "Reversed (refund)",
  adjustment: "Adjustment",
};

export default function SellerEarnings() {
  const qc = useQueryClient();
  const [payoutEmail, setPayoutEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery<EarningsResponse>({
    queryKey: ["/api/marketplace/earnings"],
  });

  const withdraw = useMutation({
    mutationFn: async (email: string) => {
      const res = await fetch("/api/marketplace/withdraw", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ payoutEmail: email }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || "Withdrawal failed");
      return json;
    },
    onSuccess: (res) => {
      setError(null);
      setNotice(
        `${money(res.amountCents)} is on its way to ${payoutEmail}. PayPal transfers usually arrive within a few minutes.`,
      );
      qc.invalidateQueries({ queryKey: ["/api/marketplace/earnings"] });
    },
    onError: (e: Error) => { setNotice(null); setError(e.message); },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="min-h-screen bg-background p-8">
        <Alert variant="destructive" className="max-w-lg mx-auto">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Could not load your earnings. Please sign in and try again.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const { balance, minPayoutCents, canWithdraw, entries } = data;
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payoutEmail);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-[9999] bg-background/95 backdrop-blur border-b">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <SidebarTrigger className="h-8 w-8" data-testid="button-sidebar-toggle" />
          <Wallet className="h-5 w-5 text-amber-500" />
          <h1 className="text-lg font-bold">Earnings</h1>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        <div className="grid gap-4 sm:grid-cols-3">
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">Available</p>
              <p className="text-2xl font-bold" data-testid="text-available-balance">
                {money(balance.availableCents)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" /> Pending
              </p>
              <p className="text-2xl font-bold" data-testid="text-pending-balance">
                {money(balance.pendingCents)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">Lifetime</p>
              <p className="text-2xl font-bold" data-testid="text-total-balance">
                {money(balance.totalCents)}
              </p>
            </CardContent>
          </Card>
        </div>

        {balance.pendingCents > 0 && (
          <Alert>
            <Clock className="h-4 w-4" />
            <AlertDescription>
              Pending commission becomes available 14 days after it is earned,
              once the referral's payment is past the refund window.
            </AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ArrowDownToLine className="h-4 w-4" /> Withdraw
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {notice && (
              <Alert data-testid="alert-withdraw-success">
                <AlertDescription>{notice}</AlertDescription>
              </Alert>
            )}
            {error && (
              <Alert variant="destructive" data-testid="alert-withdraw-error">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="payoutEmail">Your PayPal email</Label>
              <Input
                id="payoutEmail"
                type="email"
                value={payoutEmail}
                onChange={(e) => setPayoutEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="input-payout-email"
              />
              <p className="text-xs text-muted-foreground">
                Double-check this — payments sent to a wrong address cannot be recovered.
              </p>
            </div>

            <Button
              className="w-full"
              disabled={!canWithdraw || !emailValid || withdraw.isPending}
              onClick={() => withdraw.mutate(payoutEmail)}
              data-testid="button-withdraw"
            >
              {withdraw.isPending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending…</>
              ) : (
                <>Withdraw {money(balance.availableCents)}</>
              )}
            </Button>

            {!canWithdraw && (
              <p className="text-xs text-muted-foreground text-center" data-testid="text-min-payout">
                Minimum withdrawal is {money(minPayoutCents)}.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">History</CardTitle>
          </CardHeader>
          <CardContent>
            {entries.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center" data-testid="text-no-entries">
                No earnings yet. Share your storefront link to start earning.
              </p>
            ) : (
              <div className="divide-y">
                {entries.map((e) => (
                  <div key={e.id} className="py-3 flex items-center justify-between gap-4" data-testid={`row-entry-${e.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">
                          {ENTRY_LABELS[e.type] ?? e.type}
                        </span>
                        {e.level && <Badge variant="secondary" className="text-xs">L{e.level}</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {new Date(e.createdAt).toLocaleDateString()}
                        {new Date(e.availableAt) > new Date() && " · held"}
                      </p>
                    </div>
                    <span
                      className={`text-sm font-semibold tabular-nums shrink-0 ${
                        e.amountCents < 0 ? "text-muted-foreground" : "text-emerald-600 dark:text-emerald-400"
                      }`}
                    >
                      {e.amountCents > 0 ? "+" : ""}{money(e.amountCents)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
